/* global google */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Circle,
  GoogleMap,
  Marker,
  Polyline,
  useJsApiLoader,
} from '@react-google-maps/api';
import Config from '@/config';
import { LocationFlagPoint } from '@/services/dependabilityV2API';
import styled from '@/styles';
import { dependabilityV2Colors } from '@/styles/colors';

/**
 * The geofence map for one flagged event (GRAVY-10365).
 *
 * STANDALONE AND REUSABLE ON PURPOSE. GRAVY-10366's Review modal embeds this exact
 * component, and its AC requires the two surfaces to show the "same anchor point, real
 * geofence radius, flagged point, connecting line, and caption pattern". A second
 * implementation could not be kept in step by review — the same reasoning that produced
 * the `QueueResolveTimesheet` adapter after Dashboard V2's Resolve Timesheet flow was
 * rebuilt twice and failed QA twice.
 *
 * The circle is sized from `geofenceRadiusFeet` — the radius STORED on the flag, which was
 * the one in force at the time — never from the client's current setting. The AC calls for
 * "that client's real configured geofence radius, not a generic circle", and a later change
 * to the client's config must not redraw a historical entry.
 */

const FEET_PER_METRE = 3.28084;

/**
 * The smallest on-screen radius, in PIXELS, the geofence circle is allowed to
 * shrink to (GRAVY-10457).
 *
 * The circle is drawn in METRES, and the map fits both points — so the further
 * the flagged point, the more it zooms out, and the geofence shrinks with it.
 * At a 1000ft radius and an 11-mile discrepancy the true circle is a fraction
 * of a pixel across in a 120px-tall frame: correctly scaled and completely
 * invisible. QA read that as "the radius circle is missing past ~2 miles",
 * which is what it looks like, and they were right that nothing was drawn to
 * see.
 *
 * A FLOOR rather than a redesign: below this size the circle stops carrying
 * scale information at all (nobody can measure a 1px ring), so keeping it
 * locatable costs nothing that was legible in the first place, and above it —
 * every close-range case, where the ratio is the whole point — the circle is
 * still exactly to scale. `data-overlays` publishes `radius_floored` so which
 * of the two happened is verifiable rather than eyeballed.
 *
 * GRAVY-10821 raised this from 7 to 10 (a 20px minimum diameter) after QA
 * reported the circle "not clearly visible" past ~2 miles. Measured against
 * this frame, a 1000ft fence draws 82px across at 1.4 miles, 41px at 2.1, and
 * only 20px at 6.2 — which reproduces their boundary exactly.
 *
 * Deliberately NOT scaled up to stay prominent at any zoom. The circle is to
 * scale, and at six miles a small circle is the TRUE statement: it says the
 * worker was thirty times the fence radius away, which is the fact ops is
 * judging. Inflating it would misreport the geofence to win a legibility
 * argument, so the stroke and fill carry that instead (see the Circle below).
 */
const MIN_CIRCLE_RADIUS_PX = 10;

/**
 * Ground distance covered by one screen pixel at a given latitude and zoom —
 * the Web Mercator constant, which is how a metre-sized overlay is converted
 * into the pixels it will actually occupy.
 */
const metresPerPixel = (lat: number, zoom: number) =>
  (156_543.03392 * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom;

const Frame = styled('div', {
  width: '100%',
  height: '120px',
  borderRadius: '8px',
  overflow: 'hidden',
  backgroundColor: dependabilityV2Colors.depSurface,
});
const Fallback = styled('div', {
  width: '100%',
  height: '120px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '$md',
  color: dependabilityV2Colors.depMetaText,
  backgroundColor: dependabilityV2Colors.depSurface,
});

const containerStyle = { width: '100%', height: '100%' };

interface Props {
  anchor: LocationFlagPoint | null;
  flagged: LocationFlagPoint | null;
  geofenceRadiusFeet: number;
  /** Distinguishes the two maps when several render in one modal. */
  testId?: string;
}

const LocationFlagMap = ({
  anchor,
  flagged,
  geofenceRadiusFeet,
  testId = 'location-flag-map',
}: Props) => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    // `Config.GOOGLE_API_KEY` is `string | undefined` (straight off process.env). Coerced
    // rather than asserted: with no key the loader never reports ready and the component
    // holds its "Loading map…" fallback, which is the right degradation for a
    // supporting visual — it must never throw and take the modal down with it.
    googleMapsApiKey: Config.GOOGLE_API_KEY ?? '',
  });

  // The zoom `fitBounds` settled on. Held in state because the geofence's
  // rendered size depends on it (see MIN_CIRCLE_RADIUS_PX) and it is not known
  // until the map has measured itself.
  const mapRef = useRef<google.maps.Map | null>(null);
  const [zoom, setZoom] = useState<number | null>(null);

  const readZoom = useCallback(() => {
    const next = mapRef.current?.getZoom();
    setZoom((prev) =>
      typeof next === 'number' && next !== prev ? next : prev,
    );
  }, []);

  // Frame both points rather than centring on one: the whole story of the entry is the
  // gap between them, and a fixed zoom would cut one off at 6 miles apart.
  const onLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      if (!anchor || !flagged) return;
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(anchor);
      bounds.extend(flagged);
      // Padding is 12, not 24. The frame is only 120px tall, so 24 on every
      // side spent 48 of those 120 pixels on margin — and `fitBounds` picks
      // from discrete zoom levels, so on a far-apart pair that was enough to
      // drop a whole level and squeeze both markers and the line into the
      // middle third of an already short frame.
      map.fitBounds(bounds, 12);
      readZoom();
    },
    [anchor, flagged, readZoom],
  );

  /**
   * Reflects the LOADED overlays onto `data-overlays`, so an end-to-end test can
   * assert the geofence is actually drawn (GRAVY-10457).
   *
   * Why this exists at all: `Circle`, `Polyline` and `Marker` are painted on the
   * map's canvas and create NO DOM, so a spec cannot see them. That is what let
   * a map with no anchor mark ship, and what made QA's `querySelector` check
   * come back empty against working code — absence of DOM is not absence of
   * overlay.
   *
   * Every value is read BACK off the Google objects (`getRadius`, `getCenter`,
   * `getPath`, `getMap`), never echoed from the props. Echoing props would only
   * restate what the Jest spec already proves; reading back proves the overlay
   * exists, is attached to a map, and holds the stored geometry.
   *
   * Not gated behind a flag: it is a few coordinates the map is already drawing
   * on screen, so there is nothing here that is not already visible, and a
   * second code path for tests is a thing that can itself break.
   */
  const instances = useRef<{
    circle?: google.maps.Circle;
    line?: google.maps.Polyline;
    anchorPin?: google.maps.Marker;
    flaggedPin?: google.maps.Marker;
  }>({});
  const [overlays, setOverlays] = useState<string | undefined>(undefined);

  // Read inside `publishOverlays`, which is a stable callback — a ref keeps the
  // latest values reachable there without making the publisher depend on them
  // (and so re-subscribe every overlay on every zoom change).
  const radiusFacts = useRef<{ true_m: number; floored: boolean }>({
    true_m: 0,
    floored: false,
  });

  const publishOverlays = useCallback(() => {
    const { circle, line, anchorPin, flaggedPin } = instances.current;
    // All four or nothing — a partial snapshot would let a spec assert against
    // a half-built map and pass.
    if (!circle || !line || !anchorPin || !flaggedPin) return;

    const point = (p?: google.maps.LatLng | null) =>
      p
        ? { lat: Number(p.lat().toFixed(6)), lng: Number(p.lng().toFixed(6)) }
        : null;
    const icon = anchorPin.getIcon() as google.maps.Symbol | undefined;

    const next = JSON.stringify({
      circle: {
        // Metres, as the API holds it. The spec converts the stored feet itself.
        radius_m: Math.round(circle.getRadius()),
        center: point(circle.getCenter()),
        on_map: !!circle.getMap(),
        // GRAVY-10457. The STORED radius, and whether what is drawn had to be
        // floored to stay visible. Published because "is the circle missing or
        // just tiny?" is the exact question three verification passes could not
        // answer from the browser — the two look identical on screen and are
        // completely different bugs.
        true_radius_m: Math.round(radiusFacts.current.true_m),
        radius_floored: radiusFacts.current.floored,
      },
      line: {
        path: line
          .getPath()
          .getArray()
          .map((p) => point(p)),
        on_map: !!line.getMap(),
      },
      anchor: {
        position: point(anchorPin.getPosition()),
        on_map: !!anchorPin.getMap(),
        // Pixel-scaled on purpose — see the anchor Marker below.
        icon_scale: typeof icon?.scale === 'number' ? icon.scale : null,
      },
      flagged: {
        position: point(flaggedPin.getPosition()),
        on_map: !!flaggedPin.getMap(),
      },
    });

    // Guarded: `setState` inside an onLoad that re-renders could otherwise loop.
    setOverlays((prev) => (prev === next ? prev : next));
  }, []);

  const captureCircle = useCallback(
    (c: google.maps.Circle) => {
      instances.current.circle = c;
      publishOverlays();
    },
    [publishOverlays],
  );
  const captureLine = useCallback(
    (l: google.maps.Polyline) => {
      instances.current.line = l;
      publishOverlays();
    },
    [publishOverlays],
  );
  const captureAnchorPin = useCallback(
    (m: google.maps.Marker) => {
      instances.current.anchorPin = m;
      publishOverlays();
    },
    [publishOverlays],
  );
  const captureFlaggedPin = useCallback(
    (m: google.maps.Marker) => {
      instances.current.flaggedPin = m;
      publishOverlays();
    },
    [publishOverlays],
  );

  // The STORED radius, in the metres the API wants. Always the number the flag
  // was raised with (GRAVY-10365) — the floor below changes what is DRAWN, and
  // never this.
  const trueRadiusMetres = geofenceRadiusFeet / FEET_PER_METRE;

  // GRAVY-10457. Floored so the geofence never renders sub-pixel and reads as
  // "no radius circle at all". `zoom` is null until the map has measured
  // itself, and with no anchor there is no latitude to convert at — either way
  // there is nothing to floor against yet, so the true radius stands.
  const minRadiusMetres =
    zoom == null || !anchor
      ? 0
      : MIN_CIRCLE_RADIUS_PX * metresPerPixel(anchor.lat, zoom);
  const renderedRadiusMetres = Math.max(trueRadiusMetres, minRadiusMetres);
  const radiusFloored = renderedRadiusMetres > trueRadiusMetres;
  radiusFacts.current = { true_m: trueRadiusMetres, floored: radiusFloored };

  // Republish once the zoom lands.
  //
  // The overlays' own `onLoad` callbacks fire on mount, BEFORE `fitBounds` has
  // reported a zoom — and React runs child effects first, so the Circle
  // publishes while `zoom` is still null. Without this the snapshot froze on
  // the pre-fit state and reported `radius_floored: false` for every entry,
  // including the far ones where the floor is the whole point.
  useEffect(() => {
    publishOverlays();
  }, [publishOverlays, renderedRadiusMetres, radiusFloored]);

  // A flag with no coordinates cannot be drawn. Say so rather than rendering an empty grey
  // box that reads as a broken tile load.
  if (!anchor || !flagged) {
    return (
      <Fallback data-testid={`${testId}-unavailable`}>
        Location unavailable
      </Fallback>
    );
  }

  if (!isLoaded) {
    return <Fallback data-testid={`${testId}-loading`}>Loading map…</Fallback>;
  }

  // `LocationFlagPoint` is already {lat, lng} — the shape the Maps SDK takes.
  const anchorLatLng = anchor;
  const flaggedLatLng = flagged;

  return (
    <Frame data-testid={testId} data-overlays={overlays}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        onLoad={onLoad}
        // `fitBounds` resolves asynchronously, so the zoom read inside `onLoad`
        // can still be the pre-fit one; these are what settle it. Both are
        // no-ops once the value stops changing (`readZoom` compares first), so
        // there is no render loop.
        onIdle={readZoom}
        onZoomChanged={readZoom}
        options={{
          disableDefaultUI: true,
          gestureHandling: 'none',
          keyboardShortcuts: false,
        }}
      >
        {/* The real radius, converted to the metres the API wants — floored
            only when it would otherwise be too small to see at all
            (GRAVY-10457).

            GRAVY-10821: weight 3 and a denser fill, because at six miles the
            to-scale circle is only ~20px across and a thin ring that size
            disappears into a busy Google tile. This is the legibility half of
            that fix — the geometry stays honest, the ink gets heavier. */}
        <Circle
          onLoad={captureCircle}
          center={anchorLatLng}
          radius={renderedRadiusMetres}
          options={{
            strokeColor: dependabilityV2Colors.depPositive,
            strokeOpacity: 1,
            strokeWeight: 3,
            fillColor: dependabilityV2Colors.depPositive,
            fillOpacity: 0.28,
            clickable: false,
          }}
        />
        {/* GRAVY-10457. The anchor's OWN mark, not just the circle's centre.
            Fixed pixel scale is the point: the circle is drawn in METRES, so at
            `fitBounds` over a distant flag it can shrink below a pixel and leave
            nothing at all marking the venue — which is exactly how this read as
            "the map never draws the anchor". A symbol stays legible at any zoom.

            A small dot in the geofence's own green rather than a second pin: it
            should read as the centre of that circle, not compete with the red
            flagged marker. The Figma mock (10366 plan §2.4) specifies only the
            circle, the line and the flagged marker, so this is a deliberate
            small addition to satisfy the 10365 AC's "map shows the anchor
            point" at every zoom. */}
        <Marker
          onLoad={captureAnchorPin}
          position={anchorLatLng}
          title="Venue"
          clickable={false}
          zIndex={1}
          icon={{
            path: google.maps.SymbolPath.CIRCLE,
            scale: 5,
            fillColor: dependabilityV2Colors.depPositive,
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          }}
        />
        {/* Dashed, as drawn in the Figma: it reads as "this is the discrepancy", not as a
            route someone travelled. */}
        <Polyline
          onLoad={captureLine}
          path={[anchorLatLng, flaggedLatLng]}
          options={{
            strokeOpacity: 0,
            clickable: false,
            icons: [
              {
                icon: {
                  path: 'M 0,-1 0,1',
                  strokeOpacity: 1,
                  strokeColor: dependabilityV2Colors.depTierMonitored,
                  scale: 2,
                },
                offset: '0',
                repeat: '8px',
              },
            ],
          }}
        />
        {/* Above the anchor dot — the flagged point is what the entry is about. */}
        <Marker
          onLoad={captureFlaggedPin}
          position={flaggedLatLng}
          zIndex={2}
        />
      </GoogleMap>
    </Frame>
  );
};

export default LocationFlagMap;
